<script setup lang="ts">
/**
 * UI-kit component
 * Category: 'Menus'
 * Name: 'MenuNavItem'
 * FIGMA: ''
 */
import type { MenuNavItemProps } from '../types/interfaces'

import { AppText } from '@shared/ui/typography'
import { AppIcon } from '@shared/ui/icons'

import { isLegacyLink } from '../utils/legacy-marker'
import { useMenuNavItem } from '../composables/menu-nav-item.hook'
import MenuSubItem from './MenuSubItem.vue'
import MenuLegacyMarker from './MenuLegacyMarker.vue'

defineOptions({
  name: 'MenuNavItem',
})

const props = defineProps<MenuNavItemProps>()

const refActive = computed<boolean>(() => props.active)
const refHasChildren = computed<boolean>(() => Boolean(props.children?.length))

const showLegacyMarker = computed<boolean>(() =>
  !props.isProduction
  && !refHasChildren.value
  && isLegacyLink({ isExternal: props.isExternal, neverMigrate: props.neverMigrate }),
)

const {
  itemRoot,
  submenu,
  isHovered,
  submenuStyle,
  setHovered,
} = useMenuNavItem({
  active: refActive,
  hasChildren: refHasChildren,
})
</script>

<template>
  <div
    ref="itemRoot"
    class="adm-nav-item"
    :class="{
      'is-simple': simple,
      'is-active': active,
      'is-hovered': isHovered,
    }"
    @mouseenter="setHovered(true)"
    @mouseleave="setHovered(false)"
  >
    <NuxtLink
      :title="simple ? text : ''"
      :to="to"
      :external="isExternal"
      class="adm-nav-item__link"
    >
      <AppIcon
        :icon="icon"
        class="adm-nav-item__icon"
      />
      <AppText
        name="body1-medium"
        :color="active || isHovered ? 'blue-300' : 'white'"
        class="adm-nav-item__text"
      >
        {{ text }}
      </AppText>
      <span
        v-if="showLegacyMarker"
        class="adm-nav-item__marker"
      >
        <MenuLegacyMarker />
      </span>
    </NuxtLink>
    <div
      v-if="children"
      ref="submenu"
      class="adm-nav-item__submenu"
      :style="submenuStyle"
    >
      <template
        v-for="child in children"
        :key="child.id"
      >
        <MenuSubItem
          v-if="child.visible"
          :active="child.active"
          :text="child.text"
          :to="child.to"
          :is-external="child.isExternal"
          :never-migrate="child.neverMigrate"
          :is-production="isProduction"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped src="../styles/_menu-nav-item.scss" />
